import {
  ArrayField,
  BooleanInput,
  Datagrid,
  Edit,
  FunctionField,
  List,
  NumberField,
  NumberInput,
  SaveButton,
  SimpleForm,
  SimpleList,
  TextField,
  Toolbar,
  required,
  useInput,
  useTranslate,
  useRecordContext,
  RaRecord,
  RowClickFunction,
  Identifier,
  TransformData,
  useDataProvider
} from 'react-admin'
import React from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
  useMediaQuery
} from '@mui/material'
import log from 'loglevel'
import { useFormContext, useWatch } from 'react-hook-form'
import { filter, flow, isEmpty, isEqual, isNil, map, split } from 'lodash/fp'
import { Theme } from '@mui/material/styles'
import { assign } from 'lodash'

import { Criterion, Analytics, PartnerUser } from '../../types'
import { TimeInput, DayMonthInput, Pagination, Title } from '../Util'
import { useNavigate } from 'react-router-dom'

log.setLevel(log.levels.DEBUG)

const ActivityCriterionItem: React.FC<{ prefix: string }> = ({ prefix }) => {
  const { setValue } = useFormContext()
  const all: boolean = useWatch({ name: `${prefix}All` })
  const criterions = useWatch({ name: `${prefix}.0` })
  const translate = useTranslate()
  React.useEffect(() => {
    if (all && !isNil(criterions)) {
      for (let i = 1; i < 12; i++) {
        setValue(`${prefix}.${i}.start`, criterions.start)
        setValue(`${prefix}.${i}.end`, criterions.end)
        setValue(`${prefix}.${i}.continuousLimit`, criterions.continuousLimit)
      }
    }
  }, [all, criterions, prefix, setValue])
  const inputs: any[] = []
  for (let i = 0; i < 12; i++) {
    inputs.push(
      <Grid container spacing={1}>
        <Grid item xs={1.5}>
          <Typography variant="h6" sx={{ marginTop: '1em' }}>
            {translate(`mine.labels.monthLabels.${i}`)}
          </Typography>
        </Grid>
        <Grid item xs={3.5}>
          <TimeInput
            source={`${prefix}.${i}.start`}
            disabled={all && i > 0}
            validate={required()}
          />
        </Grid>
        <Grid item xs={3.5}>
          <TimeInput
            source={`${prefix}.${i}.end`}
            disabled={all && i > 0}
            validate={required()}
          />
        </Grid>
        <Grid item xs={3.5}>
          <NumberInput
            source={`${prefix}.${i}.continuousLimit`}
            disabled={all && i > 0}
            validate={required()}
          />
        </Grid>
      </Grid>
    )
  }
  return <>{inputs}</>
}
const ElectricityUsageCriterionItem: React.FC<{ prefix: string }> = ({
  prefix
}) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const criterions = useWatch({ name: prefix })
  const topCriterion = useWatch({ name: `${prefix}.0` })
  const inputs: JSX.Element[] = []
  const { setValue } = useFormContext()
  React.useEffect(() => {
    if (!isNil(criterions)) {
      for (let i = 1; i < 12; i++) {
        setValue(`${prefix}.${i}.continuousLimit`, topCriterion.continuousLimit)
        setValue(`${prefix}.${i}.threshold`, topCriterion.threshold)
      }
    }
  }, [topCriterion, prefix, setValue])
  for (let i = 0; i < 12; i++) {
    const scopedFormData: Criterion = !isNil(criterions)
      ? criterions[i]
      : undefined
    const enabled =
      !isNil(scopedFormData) &&
      !isNil(scopedFormData.enabled) &&
      scopedFormData.enabled
    inputs.push(
      isDesktop ? (
        <React.Fragment key={`EUCItem${i}`}>
          <Grid container justifyContent="center" spacing={1}>
            <Grid item md={0.6}>
              <BooleanInput
                source={`${prefix}.${i}.enabled`}
                defaultValue={false}
                label=""
                sx={{ marginTop: '1em' }}
              />
            </Grid>
            <Grid item md={2.25}>
              <DayMonthInput
                source={`${prefix}.${i}.startDate`}
                validate={enabled ? required() : undefined}
              />
            </Grid>
            <Grid item md={2.25}>
              <DayMonthInput
                source={`${prefix}.${i}.endDate`}
                validate={enabled ? required() : undefined}
              />
            </Grid>
            <Grid item md={2.25}>
              <TimeInput
                source={`${prefix}.${i}.start`}
                validate={enabled ? required() : undefined}
              />
            </Grid>
            <Grid item md={2.25}>
              <TimeInput
                source={`${prefix}.${i}.end`}
                validate={enabled ? required() : undefined}
              />
            </Grid>
          </Grid>
        </React.Fragment>
      ) : (
        <React.Fragment key={`EUCItem${i}`}>
          <Grid container justifyContent="space-between">
            <Grid item xs>
              <Typography variant="inherit" sx={{ marginTop: '1.5em' }}>
                {`${translate('mine.criterions.criterion')}`}
                {String.fromCharCode('①'.charCodeAt(0) + i)}
              </Typography>
            </Grid>
            <Grid item xs={1.5} justifySelf="right">
              <BooleanInput
                source={`${prefix}.${i}.enabled`}
                defaultValue={false}
                label=""
                sx={{ marginTop: '1em' }}
              />
            </Grid>
          </Grid>
          <Grid container justifyContent="center" spacing={1}>
            <Grid item xs={5}>
              <DayMonthInput
                source={`${prefix}.${i}.startDate`}
                validate={enabled ? required() : undefined}
              />
            </Grid>
            <Grid item xs={5}>
              <DayMonthInput
                source={`${prefix}.${i}.endDate`}
                validate={enabled ? required() : undefined}
              />
            </Grid>
            <Grid item xs={5}>
              <TimeInput
                source={`${prefix}.${i}.start`}
                validate={enabled ? required() : undefined}
              />
            </Grid>
            <Grid item xs={5}>
              <TimeInput
                source={`${prefix}.${i}.end`}
                validate={enabled ? required() : undefined}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
        </React.Fragment>
      )
    )
  }
  return <>{inputs}</>
}
const CriterionEditToolbar: React.FC = () => (
  <Toolbar>
    <SaveButton label="mine.user.createSave" />
  </Toolbar>
)

export const CriterionInputDialog: React.FC<{
  open: boolean
  setOpen: (state: boolean) => void
  source: string
  analytics: string
}> = ({ open, setOpen, source, analytics, ...props }) => {
  const translate = useTranslate()
  const handleClose = (): void => {
    setOpen(false)
  }
  const handleDone = (): void => {
    setOpen(false)
  }
  return (
    <Dialog onClose={handleClose} open={open} maxWidth="md" fullWidth>
      <DialogTitle>{translate('mine.labels.criterionInput.title')}</DialogTitle>
      {analytics === Analytics.activity ? (
        <DialogContent>
          <BooleanInput
            {...props}
            label={translate('mine.labels.criterionInput.all')}
            source={`${source}All`}
          />
          <ActivityCriterionItem {...props} prefix={source} />
        </DialogContent>
      ) : (
        <DialogContent>
          <ElectricityUsageCriterionItem {...props} prefix={source} />
        </DialogContent>
      )}
      <DialogActions>
        <Button variant="outlined" onClick={handleDone}>
          {translate('mine.labels.criterionInput.done')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
export const CriterionInput: React.FC<{
  source: string
  analytics: string
}> = ({ source, analytics, ...props }) => {
  const checkCriterionInput = (values: Criterion[]): string | undefined => {
    if (isNil(values)) {
      return 'required'
    }
    if (analytics === Analytics.activity) {
      if (
        !(flow([
          filter((item: Criterion) => isNil(item.start) || isNil(item.end)),
          isEmpty
        ])(values) as boolean)
      ) {
        return 'required'
      }
    }
    if (analytics === Analytics.electricityUsage) {
      if (
        !(flow([
          filter(
            (item: Criterion) =>
              !isNil(item.enabled) &&
              item.enabled &&
              (isNil(item.start) ||
                isNil(item.end) ||
                isNil(item.startDate) ||
                isNil(item.endDate))
          ),
          isEmpty
        ])(values) as boolean)
      ) {
        return 'required'
      }
    }
    return undefined
  }
  const { id } = useInput({ source, validate: checkCriterionInput })
  const translate = useTranslate()
  const [open, setOpen] = React.useState(false)
  const handleClick = (): void => {
    setOpen(true)
  }
  return (
    <div id={id}>
      <CriterionInputDialog
        {...props}
        source={source}
        analytics={analytics}
        open={open}
        setOpen={setOpen}
      />
      <Button
        variant="outlined"
        onClick={handleClick}
        sx={{ marginTop: '1em' }}>
        {translate('mine.labels.criterionInput.open')}
      </Button>
    </div>
  )
}
export const CriterionField: React.FC<{ source: string }> = ({ source }) => {
  const record = useRecordContext()
  const [data, setData] = React.useState<any | undefined>(undefined)
  const translate = useTranslate()
  React.useEffect(() => {
    if (isNil(data)) {
      const criterions = record[source]
      const items: any[] = []
      // @ts-expect-error
      map.convert({ cap: false })((item: Criterion, index: number) =>
        items.push({
          month: translate(`mine.labels.monthLabels.${index}`),
          start: item.start,
          end: item.end,
          continuousLimit: item.continuousLimit
        })
      )(criterions)
      setData({ criterions: items })
    }
  }, [data, record, source, translate])
  return (
    <ArrayField record={data} source="criterions">
      <Datagrid bulkActionButtons={false}>
        <TextField source="month" label="mine.criterions.month" />
        <TextField source="start" label="mine.criterions.start" />
        <TextField source="end" label="mine.criterions.end" />
        <NumberField
          source="continuousLimit"
          label="mine.criterions.continuousLimit"
        />
      </Datagrid>
    </ArrayField>
  )
}
export const CriterionComponent: React.FC<{ id: string }> = ({ id }) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const navigate = useNavigate()
  const dataProvider = useDataProvider()
  const onSuccess = (): void => {
    navigate(-1)
  }
  const applyAll = async (
    partner: string,
    own: string,
    criterionsOwn: Criterion[],
    criterionsAllOwn: boolean
  ): Promise<void> => {
    const { data: users } = await dataProvider.getList('users', {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
      filter: { partner }
    })
    for (const user of users) {
      const { id } = user as PartnerUser
      if (id === own) continue
      const input = JSON.parse(JSON.stringify(user))
      assign(input, { criterions: criterionsOwn })
      assign(input, { criterionsAll: criterionsAllOwn })
      await dataProvider.update('users', {
        id,
        data: input,
        previousData: user
      })
    }
  }
  const transform: TransformData = async (
    data: PartnerUser,
    // @ts-expect-error
    { previousData }
  ): Promise<PartnerUser> => {
    const { criterions, criterionsAll, partner, id } = data
    let { criterionsOwn } = data
    if (isNil(criterionsOwn) || !criterionsOwn) {
      criterionsOwn = !isEqual(criterions)(previousData.criterions)
      if (criterionsOwn) {
        assign(data, { criterionsOwn })
      } else {
        assign(data, { criterions: [] })
      }
    }
    if (
      criterionsOwn &&
      !isNil(criterionsAll) &&
      criterionsAll &&
      criterionsAll !== previousData.criterionsApplyAll
    ) {
      await applyAll(partner, id, criterions, criterionsAll as boolean)
    }
    log.debug('[CriterionComponent#transform]', data)
    return data
  }
  return (
    <Edit
      resource="users"
      id={id}
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess }}
      transform={transform}
      title={<Title title="mine.criterions.title" />}>
      <SimpleForm toolbar={<CriterionEditToolbar />}>
        <BooleanInput source="criterionsAll" />
        <ElectricityUsageCriterionItem prefix="criterions" />
        {!isDesktop && <div style={{ marginBottom: '5em' }} />}
      </SimpleForm>
    </Edit>
  )
}
export const CriterionList: React.FC<{ id: string }> = ({ id }) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const getHouseName = (name: string): string => {
    const names = split('#')(name)
    return names[1] as string
  }
  const criterionEdit: RowClickFunction = (id: Identifier): string =>
    `/criterionEdit/${id}`
  const criterionEditSimple = (record: RaRecord): string =>
    `/criterionEdit/${record.id}`
  return (
    <List
      perPage={10}
      pagination={<Pagination />}
      actions={false}
      filter={{
        partner: id
      }}
      resource="users"
      title={<Title title="mine.users.criterions" />}>
      {isDesktop ? (
        <>
          <Pagination withPerPage />
          <Datagrid rowClick={criterionEdit} bulkActionButtons={false}>
            <FunctionField
              source="name"
              label="mine.users.house"
              render={(record: PartnerUser) => {
                const houseName = getHouseName(record.name)
                return <Typography variant="inherit">{houseName}</Typography>
              }}
            />
            <TextField source="spin" sortable={false} />
          </Datagrid>
        </>
      ) : (
        <>
          <SimpleList
            primaryText={(record) => getHouseName(record.name)}
            linkType={criterionEditSimple}
          />
        </>
      )}
    </List>
  )
}
